import {useState} from 'react';
import {Navigate} from 'react-router-dom';
import {AlertCircle, Check, ChevronDown, Plus, X} from 'lucide-react';
import {cn} from '@common-web/lib/utils';
import {POSTHOG_UNMASK_CLASS} from '@common-web/lib/analytics';
import {useEnrollSteps} from '@client/pages/public/enroll/enrollStepsContext';
import {formatUsd, getPlan} from '@client/pages/public/enroll/enrollModel';
import {COVERAGE_TIER_LABELS} from '@client/pages/public/enroll/types';
import type {PlanConfig, ProductConfig} from '@client/pages/public/enroll/types';
import {TierSelect} from '@client/pages/public/enroll/components/TierSelect';
import {sitePayPeriod} from '@common/enrollmentSite/payPeriod';
import {useEnrollGroup} from '@client/pages/public/enroll/enrollContext';
import {useLinkHref} from '@client/pages/public/enroll/enrollPreview';

function FixedIndemnityDisclosure() {
  return (
    <div className='flex gap-3 rounded-lg border border-neutral-200 p-4'>
      <AlertCircle className='mt-0.5 h-5 w-5 shrink-0 text-amber-500' />
      <div className='space-y-3 text-sm text-neutral-600'>
        <p className='font-semibold text-neutral-900'>
          IMPORTANT: This is a fixed-indemnity policy, NOT health insurance.
        </p>
        <p>
          This policy may pay you a limited dollar amount if you're sick, hospitalized, disabled, or diagnosed with a
          covered condition.{' '}
          <span className='font-semibold text-neutral-900'>You're still responsible for paying the cost of your care.</span>{' '}
          The payment isn't based on the size of your medical bill, and there may be a yearly payment limit.
        </p>
        <p>
          This policy{' '}
          <span className='font-semibold text-neutral-900'>isn't a substitute for comprehensive health insurance</span>.
          It doesn't have to include most federal consumer protections that apply to health insurance.
        </p>
        <p className='text-xs text-neutral-500'>
          Looking for comprehensive coverage? Visit{' '}
          <a
            href='https://www.healthcare.gov'
            target='_blank'
            rel='noreferrer'
            className='font-medium text-[var(--enroll-accent)] hover:underline'
          >
            HealthCare.gov
          </a>{' '}
          or call <span className='font-semibold text-neutral-700'>1-800-318-2596</span> (TTY: 1-855-889-4325). For
          questions or complaints, contact your state Department of Insurance via{' '}
          <a
            href='https://www.naic.org'
            target='_blank'
            rel='noreferrer'
            className='font-medium text-[var(--enroll-accent)] hover:underline'
          >
            naic.org
          </a>
          .
        </p>
      </div>
    </div>
  );
}

/**
 * A protection product as a full-width list row with an Add/Remove toggle and an expandable
 * details tray. Unlike the medical/dental/vision steps, protection is opt-in ("add any, all, or
 * none") — multiple products share one ungated step, so there's no decline button or required
 * selection.
 */
function ProtectionRow({product, plan}: {product: ProductConfig; plan: PlanConfig}) {
  const {getTier, selections, selectPlan, clearSelection} = useEnrollSteps();
  const payPeriod = sitePayPeriod(useEnrollGroup());
  const [open, setOpen] = useState(false);
  const linkHref = useLinkHref();
  const selected = Boolean(selections[product.category]?.planId);
  const tierSelectable = product.tierSelectable !== false;
  const tier = tierSelectable ? getTier(product.category) : 'employee';
  const price = plan.rates[tier];

  return (
    <div
      className={cn('rounded-xl border border-neutral-200 bg-white transition-shadow', selected && 'shadow-sm')}
    >
      <div className='flex items-start gap-4 p-5'>
        <span
          aria-hidden
          className={cn(
            'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
            selected ? 'text-white' : 'bg-neutral-100 text-neutral-500',
          )}
          style={selected ? {backgroundColor: 'var(--enroll-accent)'} : undefined}
        >
          {selected ? <Check className='h-4 w-4' /> : <Plus className='h-4 w-4' />}
        </span>

        <div className='min-w-0 flex-1'>
          <div className='flex flex-wrap items-center gap-2'>
            <h3 className='text-base font-semibold text-neutral-900'>{plan.name}</h3>
            {selected && (
              <span className='inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700'>
                <Check className='h-3 w-3' />
                Added to your cart
              </span>
            )}
          </div>
          {plan.tagline && <p className='mt-0.5 text-sm text-neutral-500'>{plan.tagline}</p>}

          {plan.highlights.length > 0 && (
            <ul className='mt-3 grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-3'>
              {plan.highlights.map((highlight) => (
                <li key={highlight} className='flex items-start gap-1.5 text-xs text-neutral-600'>
                  <Check className='mt-0.5 h-3.5 w-3.5 shrink-0 text-green-600' />
                  <span>{highlight}</span>
                </li>
              ))}
            </ul>
          )}

          {product.note && <p className='mt-3 text-xs text-neutral-500'>{product.note}</p>}

          {tierSelectable && (
            <div className='mt-3 flex flex-wrap items-center gap-2'>
              <span className='text-xs font-medium text-neutral-500'>Coverage</span>
              <TierSelect category={product.category} size='compact' />
            </div>
          )}

          <div className='mt-3 flex flex-wrap items-center gap-x-4 gap-y-1'>
            <button
              type='button'
              onClick={() => setOpen((value) => !value)}
              aria-expanded={open}
              className='inline-flex items-center gap-1 text-xs font-medium text-[var(--enroll-accent)] underline-offset-4 hover:underline'
            >
              {open ? 'Hide details' : 'See full details'}
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
            </button>
            {plan.sbcUrl && (
              <a
                href={linkHref(plan.sbcUrl)}
                target='_blank'
                rel='noreferrer'
                className='text-xs font-medium text-neutral-500 underline-offset-4 transition-colors hover:text-[var(--enroll-ink)] hover:underline'
              >
                View benefit summary (PDF)
              </a>
            )}
          </div>
        </div>

        <div className='flex shrink-0 flex-col items-end gap-2 text-right'>
          <div>
            <div className='text-2xl font-bold text-[var(--enroll-ink)]'>{formatUsd(price)}</div>
            <div className='text-xs text-neutral-500'>{payPeriod.each}</div>
            {tierSelectable && <div className='text-xs text-neutral-400'>{COVERAGE_TIER_LABELS[tier]}</div>}
          </div>
          <div className='flex min-w-28 justify-end'>
            {selected ? (
              <button
                type='button'
                onClick={() => clearSelection(product.category)}
                className='inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-4 text-sm font-semibold text-neutral-700 transition-colors hover:bg-neutral-50'
              >
                <X className='h-4 w-4' />
                Remove
              </button>
            ) : (
              <button
                type='button'
                onClick={() => selectPlan(product.category, plan.id)}
                className='inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-transparent bg-[var(--enroll-accent)] px-5 text-sm font-semibold text-white transition-colors hover:bg-[var(--enroll-accent-hover)]'
              >
                <Plus className='h-4 w-4' />
                Add
              </button>
            )}
          </div>
        </div>
      </div>

      {open && (
        <div className='space-y-5 border-t border-neutral-200 px-5 py-5'>
          <FixedIndemnityDisclosure />

          {plan.detailRows && plan.detailRows.length > 0 && (
            <div>
              <p className='mb-1 text-xs font-semibold uppercase tracking-wide text-neutral-400'>What's covered</p>
              <div className='grid grid-cols-1 gap-x-10 sm:grid-cols-2'>
                {plan.detailRows.map((row) => (
                  <div
                    key={row.label}
                    className='flex items-start justify-between gap-4 border-t border-neutral-200 py-2 text-sm'
                  >
                    <span className='text-neutral-500'>{row.label}</span>
                    <span className='text-right font-semibold text-neutral-900'>{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ProtectionStep() {
  const {group, steps} = useEnrollSteps();
  const protectionProducts = group.products.filter((product) => product.inlineWith === 'protection');

  if (protectionProducts.length === 0) {
    return <Navigate to={`/${group.slug}/${steps[0]}`} replace />;
  }

  return (
    <div className={cn('space-y-6', POSTHOG_UNMASK_CLASS)}>
      <div className='space-y-4'>
        {protectionProducts.map((product) => {
          const plan = getPlan(group, product.planIds[0]);
          if (!plan) return null;
          return <ProtectionRow key={product.category} product={product} plan={plan} />;
        })}
      </div>

      <p className='rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800'>
        <span className='font-semibold'>Heads up:</span> these are voluntary, fixed-indemnity products — they're not
        health insurance. The federal disclosure is shown when you expand each card's full details.
      </p>
    </div>
  );
}
